import {
  Computer,
  ComputerSelection,
  ComputerStats,
} from "../../types/computer";
import { COMPUTERS } from "../../constants/computer";

export interface ComputerCalculationInput {
  computer: ComputerSelection;
}

export interface ComputerCalculationOutput {
  success: boolean;
  data?: ComputerStats;
  errors?: string[];
}

/**
 * Calculate the cost of a computer with options
 */
export function calculateComputerCost(computer: Computer): number {
  if (!computer) return 0;

  const baseComputer = COMPUTERS[computer.model];
  if (!baseComputer) return 0;

  let cost = baseComputer.baseCost;
  const hasJump = computer.options.includes("jumpControl");
  const hasHardened = computer.options.includes("hardened");
  if (hasJump && hasHardened) {
    cost *= 2.0;
  } else if (hasJump || hasHardened) {
    cost *= 1.5;
  }
  return cost;
}

/**
 * Calculate the effective processing power for Jump Control programs
 */
export function calculateJumpControlProcessing(computer: Computer): number {
  if (!computer) return 0;

  const baseComputer = COMPUTERS[computer.model];
  if (!baseComputer) return 0;

  let processing = baseComputer.processing;

  // Apply Jump Control bonus
  if (
    computer.options.includes("jumpControl") ||
    computer.options.includes("both")
  ) {
    processing += 5;
  }

  return processing;
}

/**
 * Calculate computer statistics
 */
export function calculateComputerStats(
  input: ComputerCalculationInput,
): ComputerCalculationOutput {
  const { computer } = input;
  const errors: string[] = [];

  // Allow empty computer selection (no computer selected yet)
  if (!computer || (!computer.primary && !computer.backup)) {
    return {
      success: true,
      data: {
        primaryCost: 0,
        backupCost: 0,
        totalCost: 0,
        primaryProcessing: 0,
        backupProcessing: 0,
        totalProcessing: 0,
        hasJumpControl: false,
        hasHardened: false,
      },
    };
  }

  // Validate computer selection
  if (computer.backup && computer.primary) {
    const primaryComputer = COMPUTERS[computer.primary.model];
    const backupComputer = COMPUTERS[computer.backup.model];

    if (backupComputer.processing >= primaryComputer.processing) {
      errors.push(
        "Backup computer must have lower processing power than primary computer",
      );
    }
  }

  // Calculate costs
  const primaryCost = computer.primary
    ? calculateComputerCost(computer.primary)
    : 0;
  const backupCost = computer.backup
    ? calculateComputerCost(computer.backup)
    : 0;
  const totalCost = primaryCost + backupCost;

  // Calculate processing power
  const primaryProcessing = computer.primary
    ? COMPUTERS[computer.primary.model].processing
    : 0;
  const backupProcessing = computer.backup
    ? COMPUTERS[computer.backup.model].processing
    : 0;
  const totalProcessing = Math.max(primaryProcessing, backupProcessing); // Can only use one at a time

  // Check for options
  const hasJumpControl = !!(
    computer.primary?.options.includes("jumpControl") ||
    computer.primary?.options.includes("both") ||
    computer.backup?.options.includes("jumpControl") ||
    computer.backup?.options.includes("both")
  );

  const hasHardened = !!(
    computer.primary?.options.includes("hardened") ||
    computer.primary?.options.includes("both") ||
    computer.backup?.options.includes("hardened") ||
    computer.backup?.options.includes("both")
  );

  const stats: ComputerStats = {
    totalCost,
    primaryCost,
    backupCost,
    totalProcessing,
    primaryProcessing,
    backupProcessing,
    hasJumpControl,
    hasHardened,
  };

  return {
    success: errors.length === 0,
    data: stats,
    errors: errors.length > 0 ? errors : undefined,
  };
}

/**
 * Validate computer selection
 */
export function validateComputerSelection(
  computer: ComputerSelection,
): string[] {
  const errors: string[] = [];

  if (!computer.primary && !computer.backup) {
    errors.push("At least one computer must be installed");
  }

  if (computer.primary && computer.backup) {
    const primaryComputer = COMPUTERS[computer.primary.model];
    const backupComputer = COMPUTERS[computer.backup.model];

    if (backupComputer.processing >= primaryComputer.processing) {
      errors.push(
        "Backup computer must have lower processing power than primary computer",
      );
    }
  }

  // Validate individual computers
  if (computer.primary) {
    const primaryErrors = validateComputer(computer.primary);
    errors.push(...primaryErrors.map((err) => `Primary computer: ${err}`));
  }

  if (computer.backup) {
    const backupErrors = validateComputer(computer.backup);
    errors.push(...backupErrors.map((err) => `Backup computer: ${err}`));
  }

  return errors;
}

/**
 * Validate individual computer
 */
export function validateComputer(computer: Computer): string[] {
  const errors: string[] = [];

  if (!computer.model) {
    errors.push("Computer model is required");
    return errors;
  }

  const computerDef = COMPUTERS[computer.model];
  if (!computerDef) {
    errors.push(`Invalid computer model: ${computer.model}`);
    return errors;
  }

  // Validate options
  if (computer.options.length > 0) {
    const validOptions = ["jumpControl", "hardened", "both"];
    for (const option of computer.options) {
      if (!validOptions.includes(option)) {
        errors.push(`Invalid computer option: ${option}`);
      }
    }

    // Check for conflicting options
    if (computer.options.includes("both") && computer.options.length > 1) {
      errors.push("Cannot combine 'both' option with other options");
    }
  }

  return errors;
}
